import Paragraph from '@tiptap/extension-paragraph';
import Heading from '@tiptap/extension-heading';
import Image from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { Plugin, PluginKey } from '@tiptap/pm/state';

// 辅助函数：检查节点是否受保护（标题或 isProtected=true 的节点）
const isProtectedNode = (node) => {
    return node.type.name === 'heading' || node.attrs?.isProtected === true;
};

// 辅助函数：检查选区范围内是否包含受保护节点
const hasProtectedNodeInRange = (doc, from, to) => {
    let hasProtected = false;
    doc.nodesBetween(from, to, (node) => {
        if (isProtectedNode(node)) {
            hasProtected = true;
            return false; // 停止遍历
        }
    });
    return hasProtected;
};

// 扩展Paragraph以支持blockId、detailId和isProtected
export const CustomParagraph = Paragraph.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            blockId: {
                default: null,
                keepOnSplit: false,
                parseHTML: (element) => element.getAttribute('data-block-id'),
                renderHTML: (attributes) => {
                    if (!attributes.blockId) {
                        return {};
                    }
                    return {
                        'data-block-id': attributes.blockId,
                    };
                },
            },
            detailId: {
                default: null,
                keepOnSplit: false,
                parseHTML: (element) => element.getAttribute('data-detail-id'),
                renderHTML: (attributes) => {
                    if (!attributes.detailId) {
                        return {};
                    }
                    return {
                        'data-detail-id': attributes.detailId,
                    };
                },
            },
            isProtected: {
                default: false,
                keepOnSplit: false,
                parseHTML: (element) => element.getAttribute('data-protected') === 'true',
                renderHTML: (attributes) => {
                    if (!attributes.isProtected) {
                        return {};
                    }
                    return {
                        'data-protected': 'true',
                        class: 'tiptap-protected',
                    };
                },
            },
        };
    },

    addKeyboardShortcuts() {
        return {
            Backspace: ({ editor }) => {
                const { state } = editor;
                const { selection } = state;
                const { $from, $to } = selection;

                // 检查是否选中了受保护节点
                if (hasProtectedNodeInRange(state.doc, $from.pos, $to.pos)) {
                    return true; // 阻止删除
                }

                // 检查当前光标所在节点是否受保护
                if ($from.parent.attrs?.isProtected) {
                    return true; // 阻止在受保护节点内操作
                }

                // 检查是否在段落开头
                if ($from.parentOffset === 0) {
                    const currentNode = $from.parent;

                    // 获取前一个节点 - 需要检查文档结构
                    const indexBefore = $from.index($from.depth - 1) - 1;
                    if (indexBefore >= 0) {
                        const prevNode = $from.node($from.depth - 1).child(indexBefore);

                        // 如果前一个节点是受保护的，阻止 Backspace
                        if (isProtectedNode(prevNode)) {
                            return true; // 阻止默认行为
                        }
                    }
                }

                return false; // 允许默认行为
            },
            Delete: ({ editor }) => {
                const { state } = editor;
                const { selection } = state;
                const { $from, $to } = selection;

                // 检查是否选中了受保护节点
                if (hasProtectedNodeInRange(state.doc, $from.pos, $to.pos)) {
                    return true; // 阻止删除
                }

                // 检查当前光标所在节点是否受保护
                if ($from.parent.attrs?.isProtected) {
                    return true; // 阻止在受保护节点内操作
                }

                // 检查光标后面是否是受保护节点
                const afterPos = $from.pos;
                const nodeAfter = state.doc.nodeAt(afterPos);
                if (nodeAfter && isProtectedNode(nodeAfter)) {
                    return true; // 阻止删除后面的受保护节点
                }

                return false; // 允许默认行为
            },
            // 阻止在选中受保护节点时粘贴
            'Mod-v': ({ editor }) => {
                const { state } = editor;
                const { selection } = state;

                // 检查当前光标所在节点是否受保护
                const { $from } = selection;
                if ($from.parent.attrs?.isProtected) {
                    return true;
                }

                if (hasProtectedNodeInRange(state.doc, selection.from, selection.to)) {
                    return true; // 阻止粘贴
                }

                return false;
            },
            // 阻止在选中受保护节点时剪切
            'Mod-x': ({ editor }) => {
                const { state } = editor;
                const { selection } = state;

                // 检查当前光标所在节点是否受保护
                const { $from } = selection;
                if ($from.parent.attrs?.isProtected) {
                    return true;
                }

                if (hasProtectedNodeInRange(state.doc, selection.from, selection.to)) {
                    return true; // 阻止剪切
                }

                return false;
            },
        };
    },

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey('preventProtectedModificationInParagraph'),
                filterTransaction: (transaction, state) => {
                    // 如果事务改变了文档
                    if (transaction.docChanged) {
                        let shouldBlock = false;

                        // 检查是否有受保护节点被删除或修改
                        state.doc.descendants((node, pos) => {
                            if (node.attrs?.isProtected) {
                                // 找到该节点在新文档中的位置
                                let foundInNew = false;
                                let contentChanged = false;

                                transaction.doc.descendants((newNode, newPos) => {
                                    if (newNode.attrs?.blockId === node.attrs.blockId) {
                                        foundInNew = true;
                                        // 检查内容是否被修改
                                        if (newNode.textContent !== node.textContent) {
                                            contentChanged = true;
                                        }
                                        return false;
                                    }
                                });

                                // 如果受保护节点被删除或内容被修改，阻止事务
                                if (!foundInNew || contentChanged) {
                                    shouldBlock = true;
                                    return false;
                                }
                            }
                        });

                        if (shouldBlock) {
                            return false;
                        }
                    }

                    return true;
                },
                props: {
                    // 拦截输入事件，防止在受保护节点内输入
                    handleTextInput: (view, from, to, text) => {
                        // 检查光标位置是否在受保护节点内
                        const $from = view.state.doc.resolve(from);
                        if ($from.parent.attrs?.isProtected) {
                            return true; // 阻止输入
                        }

                        if (hasProtectedNodeInRange(view.state.doc, from, to)) {
                            return true; // 阻止输入
                        }

                        return false;
                    },
                    // 拦截粘贴事件
                    handlePaste: (view, event, slice) => {
                        const { selection } = view.state;

                        // 检查光标位置是否在受保护节点内
                        const { $from } = selection;
                        if ($from.parent.attrs?.isProtected) {
                            event.preventDefault();
                            return true;
                        }

                        if (hasProtectedNodeInRange(view.state.doc, selection.from, selection.to)) {
                            event.preventDefault();
                            return true; // 阻止粘贴
                        }

                        return false;
                    },
                    // 阻止点击受保护节点时设置光标
                    handleDOMEvents: {
                        mousedown: (view, event) => {
                            const pos = view.posAtCoords({
                                left: event.clientX,
                                top: event.clientY,
                            });
                            if (pos) {
                                const $pos = view.state.doc.resolve(pos.pos);
                                if ($pos.parent.attrs?.isProtected) {
                                    event.preventDefault();
                                    return true;
                                }
                            }
                            return false;
                        },
                        // 阻止拖拽受保护节点
                        dragstart: (view, event) => {
                            const pos = view.posAtCoords({
                                left: event.clientX,
                                top: event.clientY,
                            });
                            if (pos) {
                                const $pos = view.state.doc.resolve(pos.pos);
                                if ($pos.parent.attrs?.isProtected) {
                                    event.preventDefault();
                                    return true;
                                }
                            }
                            return false;
                        },
                    },
                },
            }),
        ];
    },
});

// 扩展Heading以支持blockId、detailId和isLowestChapter
export const CustomHeading = Heading.extend({
    // 标记为原子节点
    atom: true,

    addKeyboardShortcuts() {
        return {
            // 阻止删除标题
            Backspace: ({ editor }) => {
                const { state } = editor;
                const { selection } = state;
                const { $from } = selection;

                // 检查光标是否在标题节点内或选中了标题
                if ($from.parent.type.name === 'heading') {
                    return true; // 阻止操作
                }

                // 检查选区是否包含标题
                let hasHeading = false;
                state.doc.nodesBetween(selection.from, selection.to, (node) => {
                    if (node.type.name === 'heading') {
                        hasHeading = true;
                        return false;
                    }
                });

                if (hasHeading) {
                    return true; // 阻止操作
                }

                return false;
            },
            Delete: ({ editor }) => {
                const { state } = editor;
                const { selection } = state;
                const { $from } = selection;

                // 检查光标是否在标题节点内或选中了标题
                if ($from.parent.type.name === 'heading') {
                    return true; // 阻止操作
                }

                // 检查选区是否包含标题
                let hasHeading = false;
                state.doc.nodesBetween(selection.from, selection.to, (node) => {
                    if (node.type.name === 'heading') {
                        hasHeading = true;
                        return false;
                    }
                });

                if (hasHeading) {
                    return true; // 阻止操作
                }

                return false;
            },
            // 阻止剪切标题
            'Mod-x': ({ editor }) => {
                const { state } = editor;
                const { selection } = state;

                let hasHeading = false;
                state.doc.nodesBetween(selection.from, selection.to, (node) => {
                    if (node.type.name === 'heading') {
                        hasHeading = true;
                        return false;
                    }
                });

                if (hasHeading) {
                    return true; // 阻止剪切
                }

                return false;
            },
            // 阻止在标题位置输入（防止替换）
            Enter: ({ editor }) => {
                const { state } = editor;
                const { selection } = state;
                const { $from } = selection;

                if ($from.parent.type.name === 'heading') {
                    return true; // 阻止在标题内换行
                }

                return false;
            },
        };
    },

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey('preventHeadingModification'),
                filterTransaction: (transaction, state) => {
                    // 如果事务来自用户交互且改变了文档（章节改稿的整体替换带 allowChapterReplace，放行）
                    if (
                        transaction.docChanged &&
                        !transaction.getMeta('paste') &&
                        !transaction.getMeta('allowChapterReplace')
                    ) {
                        let shouldBlock = false;

                        // 检查是否有标题节点被删除或修改
                        state.doc.descendants((node, pos) => {
                            if (node.type.name === 'heading') {
                                // 找到该标题在新文档中的位置
                                let foundInNew = false;
                                let contentChanged = false;

                                transaction.doc.descendants((newNode, newPos) => {
                                    if (
                                        newNode.type.name === 'heading' &&
                                        newNode.attrs.blockId === node.attrs.blockId
                                    ) {
                                        foundInNew = true;
                                        // 检查内容是否被修改
                                        if (newNode.textContent !== node.textContent) {
                                            contentChanged = true;
                                        }
                                        return false;
                                    }
                                });

                                // 如果标题被删除或内容被修改，阻止事务
                                if (!foundInNew || contentChanged) {
                                    shouldBlock = true;
                                    return false;
                                }
                            }
                        });

                        if (shouldBlock) {
                            return false;
                        }
                    }

                    return true;
                },
                props: {
                    handleDOMEvents: {
                        // 阻止点击标题时设置光标
                        mousedown: (view, event) => {
                            const pos = view.posAtCoords({
                                left: event.clientX,
                                top: event.clientY,
                            });
                            if (pos) {
                                const $pos = view.state.doc.resolve(pos.pos);
                                if ($pos.parent.type.name === 'heading') {
                                    event.preventDefault();
                                    return true;
                                }
                            }
                            return false;
                        },
                        // 阻止拖拽标题
                        dragstart: (view, event) => {
                            const pos = view.posAtCoords({
                                left: event.clientX,
                                top: event.clientY,
                            });
                            if (pos) {
                                const $pos = view.state.doc.resolve(pos.pos);
                                if ($pos.parent.type.name === 'heading') {
                                    event.preventDefault();
                                    return true;
                                }
                            }
                            return false;
                        },
                    },
                },
            }),
        ];
    },

    addAttributes() {
        return {
            ...this.parent?.(),
            blockId: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-block-id'),
                renderHTML: (attributes) => {
                    if (!attributes.blockId) return {};
                    return { 'data-block-id': attributes.blockId };
                },
            },
            detailId: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-detail-id'),
                renderHTML: (attributes) => {
                    if (!attributes.detailId) return {};
                    return { 'data-detail-id': attributes.detailId };
                },
            },
            isLowestChapter: {
                default: false,
                parseHTML: (element) => element.getAttribute('data-is-lowest-chapter') === 'true',
                renderHTML: (attributes) => {
                    if (!attributes.isLowestChapter) return {};
                    return { 'data-is-lowest-chapter': 'true' };
                },
            },
            isProtected: {
                default: false,
                parseHTML: (element) => element.getAttribute('data-protected') === 'true',
                renderHTML: (attributes) => {
                    if (!attributes.isProtected) return {};
                    return { 'data-protected': 'true' };
                },
            },
            chapterScope: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-chapter-scope'),
                renderHTML: (attributes) => {
                    if (!attributes.chapterScope) return {};
                    return { 'data-chapter-scope': attributes.chapterScope };
                },
            },
            // 需要存储文本内容，因为 atom 节点不使用 contentDOM
            text: {
                default: '',
                parseHTML: (element) => element.textContent || '',
            },
        };
    },

    addNodeView() {
        return ({ node, getPos, editor }) => {
            const container = document.createElement('div');
            container.className = 'tiptap-heading-wrapper';
            container.style.position = 'relative';

            // 如果是受保护的标题，添加保护类
            if (node.attrs.isProtected) {
                container.classList.add('tiptap-protected');
            }

            const heading = document.createElement(`h${node.attrs.level}`);
            heading.contentEditable = 'false';
            heading.style.cursor = 'default';
            heading.style.userSelect = 'none';

            // 手动设置文本内容（因为没有 contentDOM）
            heading.textContent = node.textContent;

            if (node.attrs.blockId) {
                heading.setAttribute('data-block-id', node.attrs.blockId);
            }
            if (node.attrs.detailId) {
                heading.setAttribute('data-detail-id', node.attrs.detailId);
            }
            if (node.attrs.isProtected) {
                heading.setAttribute('data-protected', 'true');
            }
            if (node.attrs.chapterScope) {
                heading.setAttribute('data-chapter-scope', node.attrs.chapterScope);
            }

            // 只有非保护的最低章节才显示重修按钮
            if (node.attrs.isLowestChapter && !node.attrs.isProtected) {
                const iconButton = document.createElement('button');
                iconButton.className = 'tiptap-chapter-rewrite-icon';
                iconButton.type = 'button';
                iconButton.setAttribute('aria-label', '重修章节');
                iconButton.innerHTML = `
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
                        <path d="M5 3v4"/>
                        <path d="M19 17v4"/>
                        <path d="M3 5h4"/>
                        <path d="M17 19h4"/>
                    </svg>
                `;

                iconButton.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    const pos = typeof getPos === 'function' ? getPos() : null;
                    const event = new CustomEvent('tiptap-chapter-rewrite', {
                        detail: {
                            node,
                            pos,
                            blockId: node.attrs.blockId,
                            detailId: node.attrs.detailId,
                            level: node.attrs.level,
                        },
                        bubbles: true,
                    });
                    container.dispatchEvent(event);
                });

                container.appendChild(iconButton);
            }

            container.appendChild(heading);

            return {
                dom: container,
                // 不返回 contentDOM，节点完全不可编辑
            };
        };
    },
});

// 扩展Image以支持blockId、detailId、isProtected和特殊图片类型（mermaid/latex）
export const CustomImage = Image.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            blockId: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-block-id'),
                renderHTML: (attributes) => {
                    if (!attributes.blockId) {
                        return {};
                    }
                    return {
                        'data-block-id': attributes.blockId,
                    };
                },
            },
            detailId: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-detail-id'),
                renderHTML: (attributes) => {
                    if (!attributes.detailId) {
                        return {};
                    }
                    return {
                        'data-detail-id': attributes.detailId,
                    };
                },
            },
            isProtected: {
                default: false,
                parseHTML: (element) => element.getAttribute('data-protected') === 'true',
                renderHTML: (attributes) => {
                    if (!attributes.isProtected) {
                        return {};
                    }
                    return {
                        'data-protected': 'true',
                        class: 'tiptap-protected',
                    };
                },
            },
            imageType: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-image-type'),
                renderHTML: (attributes) => {
                    if (!attributes.imageType) {
                        return {};
                    }
                    return {
                        'data-image-type': attributes.imageType,
                    };
                },
            },
            originalCode: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-original-code'),
                renderHTML: (attributes) => {
                    if (!attributes.originalCode) {
                        return {};
                    }
                    return {
                        'data-original-code': attributes.originalCode,
                    };
                },
            },
        };
    },

    addKeyboardShortcuts() {
        return {
            Backspace: ({ editor }) => {
                const { state } = editor;
                const { selection } = state;

                // 检查选区是否包含受保护的图片
                if (hasProtectedNodeInRange(state.doc, selection.from, selection.to)) {
                    return true;
                }

                return false;
            },
            Delete: ({ editor }) => {
                const { state } = editor;
                const { selection } = state;

                if (hasProtectedNodeInRange(state.doc, selection.from, selection.to)) {
                    return true;
                }

                return false;
            },
        };
    },
});

// 扩展Table以支持blockId、detailId、isProtected和tableContent
export const CustomTable = Table.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            blockId: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-block-id'),
                renderHTML: (attributes) => {
                    if (!attributes.blockId) {
                        return {};
                    }
                    return {
                        'data-block-id': attributes.blockId,
                    };
                },
            },
            detailId: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-detail-id'),
                renderHTML: (attributes) => {
                    if (!attributes.detailId) {
                        return {};
                    }
                    return {
                        'data-detail-id': attributes.detailId,
                    };
                },
            },
            isProtected: {
                default: false,
                parseHTML: (element) => element.getAttribute('data-protected') === 'true',
                renderHTML: (attributes) => {
                    if (!attributes.isProtected) {
                        return {};
                    }
                    return {
                        'data-protected': 'true',
                        class: 'tiptap-protected',
                    };
                },
            },
            tableContent: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-table-content'),
                renderHTML: (attributes) => {
                    if (!attributes.tableContent) {
                        return {};
                    }
                    return {
                        'data-table-content': attributes.tableContent,
                    };
                },
            },
        };
    },

    addKeyboardShortcuts() {
        return {
            Backspace: ({ editor }) => {
                const { state } = editor;
                const { selection } = state;

                if (hasProtectedNodeInRange(state.doc, selection.from, selection.to)) {
                    return true;
                }

                return false;
            },
            Delete: ({ editor }) => {
                const { state } = editor;
                const { selection } = state;

                if (hasProtectedNodeInRange(state.doc, selection.from, selection.to)) {
                    return true;
                }

                return false;
            },
        };
    },

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey('preventProtectedTableModification'),
                filterTransaction: (transaction, state) => {
                    if (transaction.docChanged) {
                        let shouldBlock = false;

                        state.doc.descendants((node, pos) => {
                            if (node.type.name === 'table' && node.attrs?.isProtected) {
                                let foundInNew = false;
                                let contentChanged = false;

                                transaction.doc.descendants((newNode, newPos) => {
                                    if (
                                        newNode.type.name === 'table' &&
                                        newNode.attrs?.blockId === node.attrs.blockId
                                    ) {
                                        foundInNew = true;
                                        if (newNode.textContent !== node.textContent) {
                                            contentChanged = true;
                                        }
                                        return false;
                                    }
                                });

                                if (!foundInNew || contentChanged) {
                                    shouldBlock = true;
                                    return false;
                                }
                            }
                        });

                        if (shouldBlock) {
                            return false;
                        }
                    }

                    return true;
                },
                props: {
                    handleDOMEvents: {
                        mousedown: (view, event) => {
                            const pos = view.posAtCoords({
                                left: event.clientX,
                                top: event.clientY,
                            });
                            if (pos) {
                                const $pos = view.state.doc.resolve(pos.pos);
                                // 检查是否点击在受保护的表格内
                                for (let depth = $pos.depth; depth >= 0; depth--) {
                                    const node = $pos.node(depth);
                                    if (node.type.name === 'table' && node.attrs?.isProtected) {
                                        event.preventDefault();
                                        return true;
                                    }
                                }
                            }
                            return false;
                        },
                    },
                },
            }),
        ];
    },
});

// 扩展BulletList以支持blockId、detailId和isProtected
export const CustomBulletList = BulletList.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            blockId: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-block-id'),
                renderHTML: (attributes) => {
                    if (!attributes.blockId) {
                        return {};
                    }
                    return {
                        'data-block-id': attributes.blockId,
                    };
                },
            },
            detailId: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-detail-id'),
                renderHTML: (attributes) => {
                    if (!attributes.detailId) {
                        return {};
                    }
                    return {
                        'data-detail-id': attributes.detailId,
                    };
                },
            },
            isProtected: {
                default: false,
                parseHTML: (element) => element.getAttribute('data-protected') === 'true',
                renderHTML: (attributes) => {
                    if (!attributes.isProtected) {
                        return {};
                    }
                    return {
                        'data-protected': 'true',
                        class: 'tiptap-protected',
                    };
                },
            },
        };
    },

    addKeyboardShortcuts() {
        return {
            Backspace: ({ editor }) => {
                const { state } = editor;
                const { selection } = state;

                if (hasProtectedNodeInRange(state.doc, selection.from, selection.to)) {
                    return true;
                }

                return false;
            },
            Delete: ({ editor }) => {
                const { state } = editor;
                const { selection } = state;

                if (hasProtectedNodeInRange(state.doc, selection.from, selection.to)) {
                    return true;
                }

                return false;
            },
        };
    },

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey('preventProtectedBulletListModification'),
                filterTransaction: (transaction, state) => {
                    if (transaction.docChanged) {
                        let shouldBlock = false;

                        state.doc.descendants((node, pos) => {
                            if (node.type.name === 'bulletList' && node.attrs?.isProtected) {
                                let foundInNew = false;
                                let contentChanged = false;

                                transaction.doc.descendants((newNode, newPos) => {
                                    if (
                                        newNode.type.name === 'bulletList' &&
                                        newNode.attrs?.blockId === node.attrs.blockId
                                    ) {
                                        foundInNew = true;
                                        if (newNode.textContent !== node.textContent) {
                                            contentChanged = true;
                                        }
                                        return false;
                                    }
                                });

                                if (!foundInNew || contentChanged) {
                                    shouldBlock = true;
                                    return false;
                                }
                            }
                        });

                        if (shouldBlock) {
                            return false;
                        }
                    }

                    return true;
                },
                props: {
                    handleDOMEvents: {
                        mousedown: (view, event) => {
                            const pos = view.posAtCoords({
                                left: event.clientX,
                                top: event.clientY,
                            });
                            if (pos) {
                                const $pos = view.state.doc.resolve(pos.pos);
                                for (let depth = $pos.depth; depth >= 0; depth--) {
                                    const node = $pos.node(depth);
                                    if (
                                        node.type.name === 'bulletList' &&
                                        node.attrs?.isProtected
                                    ) {
                                        event.preventDefault();
                                        return true;
                                    }
                                }
                            }
                            return false;
                        },
                    },
                },
            }),
        ];
    },
});

// 扩展OrderedList以支持blockId、detailId和isProtected
export const CustomOrderedList = OrderedList.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            blockId: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-block-id'),
                renderHTML: (attributes) => {
                    if (!attributes.blockId) {
                        return {};
                    }
                    return {
                        'data-block-id': attributes.blockId,
                    };
                },
            },
            detailId: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-detail-id'),
                renderHTML: (attributes) => {
                    if (!attributes.detailId) {
                        return {};
                    }
                    return {
                        'data-detail-id': attributes.detailId,
                    };
                },
            },
            isProtected: {
                default: false,
                parseHTML: (element) => element.getAttribute('data-protected') === 'true',
                renderHTML: (attributes) => {
                    if (!attributes.isProtected) {
                        return {};
                    }
                    return {
                        'data-protected': 'true',
                        class: 'tiptap-protected',
                    };
                },
            },
        };
    },

    addKeyboardShortcuts() {
        return {
            Backspace: ({ editor }) => {
                const { state } = editor;
                const { selection } = state;

                if (hasProtectedNodeInRange(state.doc, selection.from, selection.to)) {
                    return true;
                }

                return false;
            },
            Delete: ({ editor }) => {
                const { state } = editor;
                const { selection } = state;

                if (hasProtectedNodeInRange(state.doc, selection.from, selection.to)) {
                    return true;
                }

                return false;
            },
        };
    },

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey('preventProtectedOrderedListModification'),
                filterTransaction: (transaction, state) => {
                    if (transaction.docChanged) {
                        let shouldBlock = false;

                        state.doc.descendants((node, pos) => {
                            if (node.type.name === 'orderedList' && node.attrs?.isProtected) {
                                let foundInNew = false;
                                let contentChanged = false;

                                transaction.doc.descendants((newNode, newPos) => {
                                    if (
                                        newNode.type.name === 'orderedList' &&
                                        newNode.attrs?.blockId === node.attrs.blockId
                                    ) {
                                        foundInNew = true;
                                        if (newNode.textContent !== node.textContent) {
                                            contentChanged = true;
                                        }
                                        return false;
                                    }
                                });

                                if (!foundInNew || contentChanged) {
                                    shouldBlock = true;
                                    return false;
                                }
                            }
                        });

                        if (shouldBlock) {
                            return false;
                        }
                    }

                    return true;
                },
                props: {
                    handleDOMEvents: {
                        mousedown: (view, event) => {
                            const pos = view.posAtCoords({
                                left: event.clientX,
                                top: event.clientY,
                            });
                            if (pos) {
                                const $pos = view.state.doc.resolve(pos.pos);
                                for (let depth = $pos.depth; depth >= 0; depth--) {
                                    const node = $pos.node(depth);
                                    if (
                                        node.type.name === 'orderedList' &&
                                        node.attrs?.isProtected
                                    ) {
                                        event.preventDefault();
                                        return true;
                                    }
                                }
                            }
                            return false;
                        },
                    },
                },
            }),
        ];
    },
});

// 扩展CodeBlockLowlight以支持blockId、detailId和isProtected
export const CustomCodeBlock = CodeBlockLowlight.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            blockId: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-block-id'),
                renderHTML: (attributes) => {
                    if (!attributes.blockId) {
                        return {};
                    }
                    return {
                        'data-block-id': attributes.blockId,
                    };
                },
            },
            detailId: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-detail-id'),
                renderHTML: (attributes) => {
                    if (!attributes.detailId) {
                        return {};
                    }
                    return {
                        'data-detail-id': attributes.detailId,
                    };
                },
            },
            isProtected: {
                default: false,
                parseHTML: (element) => element.getAttribute('data-protected') === 'true',
                renderHTML: (attributes) => {
                    if (!attributes.isProtected) {
                        return {};
                    }
                    return {
                        'data-protected': 'true',
                        class: 'tiptap-protected',
                    };
                },
            },
        };
    },

    addKeyboardShortcuts() {
        return {
            Backspace: ({ editor }) => {
                const { state } = editor;
                const { selection } = state;
                const { $from } = selection;

                // 检查当前是否在受保护的代码块内
                if ($from.parent.attrs?.isProtected) {
                    return true;
                }

                if (hasProtectedNodeInRange(state.doc, selection.from, selection.to)) {
                    return true;
                }

                return false;
            },
            Delete: ({ editor }) => {
                const { state } = editor;
                const { selection } = state;
                const { $from } = selection;

                if ($from.parent.attrs?.isProtected) {
                    return true;
                }

                if (hasProtectedNodeInRange(state.doc, selection.from, selection.to)) {
                    return true;
                }

                return false;
            },
        };
    },

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey('preventProtectedCodeBlockModification'),
                filterTransaction: (transaction, state) => {
                    if (transaction.docChanged) {
                        let shouldBlock = false;

                        state.doc.descendants((node, pos) => {
                            if (node.type.name === 'codeBlock' && node.attrs?.isProtected) {
                                let foundInNew = false;
                                let contentChanged = false;

                                transaction.doc.descendants((newNode, newPos) => {
                                    if (
                                        newNode.type.name === 'codeBlock' &&
                                        newNode.attrs?.blockId === node.attrs.blockId
                                    ) {
                                        foundInNew = true;
                                        if (newNode.textContent !== node.textContent) {
                                            contentChanged = true;
                                        }
                                        return false;
                                    }
                                });

                                if (!foundInNew || contentChanged) {
                                    shouldBlock = true;
                                    return false;
                                }
                            }
                        });

                        if (shouldBlock) {
                            return false;
                        }
                    }

                    return true;
                },
                props: {
                    handleTextInput: (view, from, to, text) => {
                        const $from = view.state.doc.resolve(from);
                        if ($from.parent.attrs?.isProtected) {
                            return true;
                        }
                        return false;
                    },
                    handleDOMEvents: {
                        mousedown: (view, event) => {
                            const pos = view.posAtCoords({
                                left: event.clientX,
                                top: event.clientY,
                            });
                            if (pos) {
                                const $pos = view.state.doc.resolve(pos.pos);
                                if (
                                    $pos.parent.type.name === 'codeBlock' &&
                                    $pos.parent.attrs?.isProtected
                                ) {
                                    event.preventDefault();
                                    return true;
                                }
                            }
                            return false;
                        },
                    },
                },
            }),
        ];
    },
});
